import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Eye, Edit, Archive, Clock } from 'lucide-react';

// @ts-nocheck
export default function ProjectGridView({
                                            projects,
                                            subsidiaries,
                                            departments,
                                            onView,
                                            onEdit,
                                            onArchive,
                                            formatBudget,
                                            statusConfig,
                                            priorityConfig
                                        }) {
    const getSubsidiary = (id) => subsidiaries.find(s => s.id === id);
    const getDepartment = (id) => departments.find(d => d.id === id);

    const getProgressColor = (progress) => {
        if (progress > 80) return "bg-green-500";
        if (progress > 50) return "bg-teal-500";
        if (progress > 20) return "bg-yellow-500";
        return "bg-gray-300";
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => {
                const status = statusConfig[project.status] || statusConfig.draft;
                const priority = priorityConfig[project.priority] || priorityConfig.medium;
                const subsidiary = getSubsidiary(project.subsidiary_id);
                const department = getDepartment(project.department_id);

                return (
                    <Card
                        key={project.id}
                        className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer group relative overflow-hidden"
                        onClick={() => onView(project)}
                    >
                        <div
                            className={`absolute left-0 top-0 w-1 h-full ${priority.indicator}`}
                        />

                        <CardHeader className="pb-3">
                            <div className="flex justify-between items-start">
                                <div className="flex-1 min-w-0">
                                    <CardTitle className="text-lg font-bold text-gray-900 group-hover:text-teal-600 transition-colors truncate">
                                        {project.name}
                                    </CardTitle>
                                    <p className="text-xs text-gray-500 mt-1">
                                        {project.code} • {new Date(project.created_date).toLocaleDateString('fr-FR')}
                                    </p>
                                </div>
                                <Badge className={`${status.color} gap-1 shrink-0`}>
                                    {status.icon}
                                    {status.label}
                                </Badge>
                            </div>
                        </CardHeader>

                        <CardContent className="space-y-4">
                            {/* Budget */}
                            <div className="text-center">
                                <p className="text-2xl font-bold text-gray-900">
                                    {formatBudget(project.total_budget)}
                                </p>
                                <p className="text-sm text-gray-500">Budget total</p>
                            </div>

                            {/* Progress Circle */}
                            <div className="flex items-center justify-center">
                                <div className="relative w-16 h-16">
                                    <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 64 64">
                                        <circle
                                            cx="32"
                                            cy="32"
                                            r="28"
                                            fill="none"
                                            stroke="rgb(229, 231, 235)"
                                            strokeWidth="6"
                                        />
                                        <circle
                                            cx="32"
                                            cy="32"
                                            r="28"
                                            fill="none"
                                            stroke="rgb(20, 83, 45)"
                                            strokeWidth="6"
                                            strokeDasharray={`${(project.progress || 0) * 1.76} 176`}
                                            className="transition-all duration-300"
                                        />
                                    </svg>
                                    <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-sm font-bold text-gray-900">
                      {project.progress || 0}%
                    </span>
                                    </div>
                                </div>
                            </div>

                            {/* Project Details */}
                            <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                    {subsidiary && (
                                        <div
                                            className="w-4 h-4 rounded-full flex items-center justify-center text-white text-xs font-bold"
                                            style={{backgroundColor: subsidiary.color}}
                                        >
                                            {subsidiary.code[0]}
                                        </div>
                                    )}
                                    <Badge variant="outline" className="text-xs">
                                        {subsidiary?.name || "Non défini"}
                                    </Badge>
                                </div>

                                <div className="flex items-center gap-2">
                                    <Badge variant="outline" className="text-xs">
                                        {department?.name || "Non défini"}
                                    </Badge>
                                </div>

                                <div className="flex items-center gap-2">
                                    <Badge className={`${priority.color} text-xs`}>
                                        {priority.label}
                                    </Badge>
                                    <span className="text-xs text-gray-500 capitalize">
                    {project.type?.replace(/_/g, ' ') || "Non défini"}
                  </span>
                                </div>
                            </div>

                            {/* Last Updated */}
                            <div className="flex items-center gap-1 text-xs text-gray-500">
                                <Clock className="w-3 h-3" />
                                Mis à jour le {new Date(project.updated_date).toLocaleDateString('fr-FR')}
                            </div>

                            {/* Quick Actions */}
                            <div className="flex gap-2 pt-2 border-t border-gray-100">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="flex-1 gap-1"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onView(project);
                                    }}
                                >
                                    <Eye className="w-3 h-3" />
                                    Voir
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="flex-1 gap-1"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onEdit(project);
                                    }}
                                >
                                    <Edit className="w-3 h-3" />
                                    Modifier
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="gap-1 text-red-600 hover:text-red-700"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onArchive(project);
                                    }}
                                >
                                    <Archive className="w-3 h-3" />
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                );
            })}

            {projects.length === 0 && (
                <div className="col-span-full text-center py-12">
                    <p className="text-gray-500">Aucun projet trouvé pour les filtres actuels</p>
                </div>
            )}
        </div>
    );
}
